import React, { useEffect, useState } from 'react';
import { Popover } from '@mui/material';
import { ScheduledAction } from '@demind-inc/core';
import {
  CheckCircle,
  CheckCircleOutline,
  Cancel,
  ExpandMore,
  ExpandLess,
  Restaurant,
  Bedtime,
  FitnessCenter,
  AutoAwesome,
  TaskAlt,
  Loop,
  CalendarMonth,
  Edit,
  Save,
  DoneAll,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import clsx from 'clsx';
import './PlanItemCard.scss';
import { useGeneralSettings } from '../../../../hooks/useGeneralSettings';
import { formatTimeFromDayjs } from '../../../../helpers';
import CustomTimeSelector from '../../../common/CustomTimeSelector/CustomTimeSelector';
import { resolveScheduleType } from '../helpers';
import RichContextRow from './RichContextRow';
import { useTranslation } from 'react-i18next';

const ICON_COLOR_BY_TYPE: Record<string, string> = {
  sleep: '#8B5CF6',
  meal: '#F97316',
  workout: '#2563EB',
  todo: '#2563EB',
  routine: '#0F766E',
  calendar: '#16A34A',
};

const DEFAULT_ICON_COLOR = '#680DDB';

const TYPE_ICONS: Record<string, React.ElementType> = {
  meal: Restaurant,
  sleep: Bedtime,
  workout: FitnessCenter,
  routine: Loop,
  todo: TaskAlt,
  calendar: CalendarMonth,
};

interface PlanItemCardProps {
  schedule: ScheduledAction;
  isAccepted: boolean;
  isDiscarded: boolean;
  isAutoAccepted?: boolean;
  onAccept: (id: string) => void;
  onDiscard: (id: string) => void;
  onUpdateTime: (id: string, startDate: string, endDate: string) => void;
}

type ReplannedScheduleMetadata = {
  beforeStartDate?: string;
  beforeEndDate?: string;
};

const PlanItemCard: React.FC<PlanItemCardProps> = ({
  schedule,
  isAccepted,
  isDiscarded,
  isAutoAccepted = false,
  onAccept,
  onDiscard,
  onUpdateTime,
}) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const rawType = resolveScheduleType(schedule);
  const iconColor = ICON_COLOR_BY_TYPE[rawType] ?? DEFAULT_ICON_COLOR;
  const ItemIcon = TYPE_ICONS[rawType] ?? AutoAwesome;
  const name = schedule.name ?? t('task.allPlan.planItem.plannedItem');
  const scheduleWithMetadata = schedule as ScheduledAction & ReplannedScheduleMetadata;

  const formatScheduleTime = (value?: string) => {
    if (!value) return '';
    return formatTimeFromDayjs(dayjs(value), timeFormat, true);
  };

  const startTime = formatScheduleTime(schedule.startDate);
  const endTime = formatScheduleTime(schedule.endDate);
  const beforeStartTime = scheduleWithMetadata.beforeStartDate
    ? formatScheduleTime(scheduleWithMetadata.beforeStartDate)
    : '';
  const beforeEndTime = scheduleWithMetadata.beforeEndDate
    ? formatScheduleTime(scheduleWithMetadata.beforeEndDate)
    : '';
  const beforeTimeRange = beforeStartTime
    ? `${beforeStartTime}${beforeEndTime ? ` – ${beforeEndTime}` : ''}`
    : '';
  const timeRange = startTime ? `${startTime}${endTime ? ` – ${endTime}` : ''}` : '';
  const isDecided = isAccepted || isDiscarded;
  const scheduleId = String(schedule.id);
  const [isExpanded, setIsExpanded] = useState(!isDecided);
  const [editAnchorEl, setEditAnchorEl] = useState<HTMLElement | null>(null);
  const [startTimeInput, setStartTimeInput] = useState<Dayjs | null>(
    schedule.startDate ? dayjs(schedule.startDate) : null
  );
  const [endTimeInput, setEndTimeInput] = useState<Dayjs | null>(
    schedule.endDate ? dayjs(schedule.endDate) : null
  );
  const isEditingTime = Boolean(editAnchorEl);
  const isInvalidTimeRange =
    !!startTimeInput && !!endTimeInput && startTimeInput.isAfter(endTimeInput);
  const shouldShowBody = !isDecided || isExpanded;
  const shouldShowActionRow = !isDecided || isAutoAccepted;
  const DecisionIcon = isAccepted ? (isAutoAccepted ? DoneAll : CheckCircle) : Cancel;

  useEffect(() => {
    setIsExpanded(!isDecided);
  }, [isDecided]);

  useEffect(() => {
    if (!isEditingTime) {
      setStartTimeInput(schedule.startDate ? dayjs(schedule.startDate) : null);
      setEndTimeInput(schedule.endDate ? dayjs(schedule.endDate) : null);
    }
  }, [isEditingTime, schedule.endDate, schedule.startDate, schedule.id]);

  const handleSaveTime = () => {
    if (!startTimeInput || !endTimeInput || !schedule.startDate || !schedule.endDate) {
      return;
    }
    if (isInvalidTimeRange) {
      return;
    }

    const startHour = startTimeInput.hour();
    const startMinute = startTimeInput.minute();
    const endHour = endTimeInput.hour();
    const endMinute = endTimeInput.minute();

    const nextStart = dayjs(schedule.startDate).hour(startHour).minute(startMinute).second(0);
    let nextEnd = dayjs(schedule.endDate).hour(endHour).minute(endMinute).second(0);

    if (nextEnd.isBefore(nextStart) || nextEnd.isSame(nextStart)) {
      nextEnd = nextEnd.add(1, 'day');
    }

    onUpdateTime(scheduleId, nextStart.toISOString(), nextEnd.toISOString());
    setEditAnchorEl(null);
  };

  const handleHeaderClick = () => {
    if (!isDecided) return;
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="plan-item-row">
      <div className="plan-item-row__icon-circle" style={{ background: iconColor }}>
        <ItemIcon className="plan-item-row__icon" />
      </div>

      <div className="plan-item-row__body">
        <div
          className={clsx('plan-item-card', {
            'plan-item-card--discarded': isDiscarded,
          })}
        >
          <span className="plan-item-card__accent-bar" style={{ background: iconColor }} />

          <div
            className="plan-item-card__header"
            onClick={handleHeaderClick}
            role={isDecided ? 'button' : undefined}
            tabIndex={isDecided ? 0 : undefined}
          >
            <span className="plan-item-card__name">{name}</span>
            {isDecided && (
              <div className="plan-item-card__header-icons">
                <DecisionIcon
                  className={clsx('plan-item-card__decision-icon', {
                    'plan-item-card__decision-icon--accepted': isAccepted,
                    'plan-item-card__decision-icon--discarded': !isAccepted,
                  })}
                />
                {isExpanded ? (
                  <ExpandLess className="plan-item-card__chevron-icon" />
                ) : (
                  <ExpandMore className="plan-item-card__chevron-icon" />
                )}
              </div>
            )}
          </div>

          <div className="plan-item-card__time-chips">
            <span className="plan-item-card__time-chip plan-item-card__time-chip--current">
              {timeRange}
            </span>
            {beforeTimeRange && (
              <span
                className="plan-item-card__time-chip plan-item-card__time-chip--previous"
                style={{ background: `${iconColor}18` }}
              >
                {beforeTimeRange}
              </span>
            )}
          </div>

          {shouldShowBody && (
            <>
              <RichContextRow schedule={schedule} />

              {shouldShowActionRow && (
                <div className="plan-item-card__actions">
                  {!isDecided && (
                    <>
                      <button
                        type="button"
                        className="plan-item-card__edit-btn"
                        onClick={(event) =>
                          setEditAnchorEl(isEditingTime ? null : event.currentTarget)
                        }
                      >
                        <Edit style={{ fontSize: 16 }} />
                      </button>
                      <button
                        type="button"
                        className="plan-item-card__action-btn plan-item-card__action-btn--discard"
                        onClick={() => onDiscard(scheduleId)}
                      >
                        <Cancel style={{ fontSize: 14 }} />
                        {t('task.common.discard')}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className={clsx('plan-item-card__action-btn', {
                      'plan-item-card__action-btn--auto-accepted': isAutoAccepted,
                      'plan-item-card__action-btn--accepted': isAccepted && !isAutoAccepted,
                      'plan-item-card__action-btn--accept': !isAccepted,
                    })}
                    onClick={() => {
                      if (isAutoAccepted) return;
                      onAccept(scheduleId);
                    }}
                    disabled={isAutoAccepted || isAccepted}
                  >
                    {isAutoAccepted ? (
                      <DoneAll style={{ fontSize: 14 }} />
                    ) : isAccepted ? (
                      <CheckCircleOutline style={{ fontSize: 14 }} />
                    ) : (
                      <CheckCircle style={{ fontSize: 14 }} />
                    )}
                    {isAutoAccepted
                      ? t('task.allPlan.planItem.autoAccepted')
                      : isAccepted
                        ? t('task.common.accepted')
                        : t('task.common.accept')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {!isDecided && isEditingTime && (
          <Popover
            open={isEditingTime}
            anchorEl={editAnchorEl}
            onClose={() => setEditAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            className="plan-item-card__time-popover"
          >
            <div className="plan-item-card__time-editor">
              <div className="plan-item-card__time-row">
                <CustomTimeSelector
                  className="plan-item-card__time-input"
                  value={startTimeInput}
                  onChange={setStartTimeInput}
                  amPm={timeFormat === '12h'}
                  size="small"
                />
                <span className="plan-item-card__time-separator">
                  {t('task.allPlan.planItem.to')}
                </span>
                <CustomTimeSelector
                  className="plan-item-card__time-input"
                  value={endTimeInput}
                  onChange={setEndTimeInput}
                  amPm={timeFormat === '12h'}
                  size="small"
                />
              </div>
              <div className="plan-item-card__time-editor-actions">
                <button
                  type="button"
                  className="plan-item-card__save-time-btn plan-item-card__save-time-btn--cancel"
                  onClick={() => setEditAnchorEl(null)}
                >
                  {t('task.common.cancel')}
                </button>
                <button
                  type="button"
                  className="plan-item-card__save-time-btn"
                  onClick={handleSaveTime}
                  disabled={!startTimeInput || !endTimeInput || isInvalidTimeRange}
                >
                  <Save style={{ fontSize: 14 }} />
                  {t('task.common.set')}
                </button>
              </div>
            </div>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default PlanItemCard;
